import React from 'react';
import { Clock, Trash2, ThumbsUp, X, Lock, ClipboardList, Archive, History, BadgeCheck, MessageSquare } from 'lucide-react';
import { Button } from '../components';
import { TARIEFMODUS_LABEL, type OnderhandelingRij, type OnderhandelingCockpitStatus } from '../services/poolOnderhandelingApi';

export const euro = (v: number | null | undefined) => (v == null ? '—' : `€ ${v.toFixed(2).replace('.', ',')}`);

export const datumTijd = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export const ROW_TINT: Record<OnderhandelingCockpitStatus, string> = {
  UITGENODIGD: 'bg-blue-50/60 border-blue-100',
  TEGENVOORSTEL: 'bg-orange-50/70 border-orange-200',
  AKKOORD: 'bg-green-50/70 border-green-200',
  ACTIEF: 'bg-emerald-50/50 border-emerald-100',
  AFGEWEZEN: 'bg-red-50/60 border-red-100',
  GESLOTEN: 'bg-gray-50 border-gray-200',
};

export const STATUS_LABEL: Record<OnderhandelingCockpitStatus, { emoji: string; label: string; className: string }> = {
  UITGENODIGD: { emoji: '🔵', label: 'Uitgenodigd', className: 'text-blue-700' },
  TEGENVOORSTEL: { emoji: '🟠', label: 'Tegenvoorstel', className: 'text-orange-700' },
  AKKOORD: { emoji: '🟢', label: 'Akkoord', className: 'text-green-700' },
  ACTIEF: { emoji: '✅', label: 'Actief', className: 'text-emerald-700' },
  AFGEWEZEN: { emoji: '🔴', label: 'Afgewezen', className: 'text-red-700' },
  GESLOTEN: { emoji: '⚪', label: 'Ingetrokken', className: 'text-gray-500' },
};

interface Props {
  r: OnderhandelingRij;
  isBezig: boolean;
  uitgeklapt: boolean;
  onToggleUitklap: () => void;
  onHerinner: () => void;
  onTrekIn: () => void;
  onAccepteerTegenbod: () => void;
  onTegenbodOpenen: () => void;
  onAfwijzenOpenen: () => void;
  onArchiveer: () => void;
  onTender: () => void;
  onOpenContract: () => void;
}

/** Eén rij van de onderhandelings-matrix — herbruikt door de platte lijst en het Pool-Dekkingsdashboard. */
export const OnderhandelingRijKaart: React.FC<Props> = ({
  r,
  isBezig,
  uitgeklapt,
  onToggleUitklap,
  onHerinner,
  onTrekIn,
  onAccepteerTegenbod,
  onTegenbodOpenen,
  onAfwijzenOpenen,
  onArchiveer,
  onTender,
  onOpenContract,
}) => {
  const statusInfo = STATUS_LABEL[r.status];
  return (
    <div className={`rounded-xl border p-4 transition-colors ${ROW_TINT[r.status]}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
        <div className="lg:w-56 flex-shrink-0">
          <p className="font-semibold text-gray-900 text-sm">{r.professional_naam}</p>
          <p className="text-xs text-gray-500 italic">{r.discipline_label}</p>
        </div>

        <div className="lg:w-32 flex-shrink-0 text-sm text-gray-700">{r.vestiging || '—'}</div>

        <div className="lg:w-28 flex-shrink-0 text-sm font-medium text-gray-900" title={TARIEFMODUS_LABEL[r.tariefmodus] || undefined}>
          {r.bod_werkgever == null ? <span className="text-xs text-gray-500 italic">marktconform</span> : euro(r.bod_werkgever)}
          {r.tegenbod_professional != null && r.status === 'TEGENVOORSTEL' && (
            <span className="block text-xs text-orange-700">{r.bod_werkgever == null ? 'voorstel' : 'tegenbod'} {euro(r.tegenbod_professional)}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${statusInfo.className}`}>
            {statusInfo.emoji} {statusInfo.label}{' '}
            <span className="font-mono text-xs text-gray-400">({r.engine_status})</span>
          </p>
          <p className="text-xs text-gray-600 truncate">{r.laatste_actie_tekst}</p>
          {r.status === 'AFGEWEZEN' && r.dekking_tekort > 0 && (
            <p className="text-xs text-red-600 font-medium mt-0.5">
              ⚠️ Tekort van {r.dekking_tekort} in deze discipline volgens de bedrijfsgrootte-staffel
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:w-auto flex-shrink-0">
          {r.status === 'UITGENODIGD' && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={!r.herinnering_mogelijk || isBezig}
                loading={isBezig}
                onClick={onHerinner}
                title={!r.herinnering_mogelijk ? 'U kunt maximaal één keer per 24 uur herinneren' : undefined}
              >
                <Clock className="w-4 h-4 mr-1.5" /> Herinner
              </Button>
              <Button size="sm" variant="danger" loading={isBezig} onClick={onTrekIn}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Trek in
              </Button>
            </>
          )}
          {r.status === 'TEGENVOORSTEL' && (
            <>
              <Button size="sm" loading={isBezig} onClick={onAccepteerTegenbod}>
                <ThumbsUp className="w-4 h-4 mr-1.5" /> Accepteer {euro(r.tegenbod_professional)}
              </Button>
              <Button size="sm" variant="secondary" onClick={onTegenbodOpenen}>
                <MessageSquare className="w-4 h-4 mr-1.5" /> Tegenbod
              </Button>
              <Button size="sm" variant="outline" onClick={onAfwijzenOpenen}>
                <X className="w-4 h-4 mr-1.5" /> Wijs af
              </Button>
            </>
          )}
          {r.status === 'AKKOORD' && (
            <Button size="sm" onClick={onOpenContract}>
              <Lock className="w-4 h-4 mr-1.5" />
              {r.engine_status === 'CONTRACT_PENDING' ? 'Bekijk Model-OvO' : 'Teken Model-OvO'}
            </Button>
          )}
          {r.status === 'ACTIEF' && (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
              <BadgeCheck className="w-4 h-4" /> Live in de pool
            </span>
          )}
          {r.status === 'AFGEWEZEN' && (
            <>
              <Button size="sm" variant="secondary" loading={isBezig} onClick={onTender} disabled={!!r.tender_vacancy_id}>
                <ClipboardList className="w-4 h-4 mr-1.5" />
                {r.tender_vacancy_id ? 'Tender geplaatst' : 'Start Open Vacature / Tender'}
              </Button>
              <Button size="sm" variant="outline" loading={isBezig} onClick={onArchiveer}>
                <Archive className="w-4 h-4" />
              </Button>
            </>
          )}
          {r.status === 'GESLOTEN' && (
            <Button size="sm" variant="outline" loading={isBezig} onClick={onArchiveer}>
              <Archive className="w-4 h-4 mr-1.5" /> Archiveer
            </Button>
          )}
          <button
            type="button"
            onClick={onToggleUitklap}
            className="text-gray-400 hover:text-gray-600 p-1.5"
            title="Tariefgeschiedenis"
          >
            <History className="w-4 h-4" />
          </button>
        </div>
      </div>

      {uitgeklapt && (
        <div className="mt-3 pt-3 border-t border-gray-200/70 space-y-1.5">
          {r.events.length === 0 ? (
            <p className="text-xs text-gray-400">Nog geen geschiedenis.</p>
          ) : (
            r.events.map((e, i) => (
              <div key={i} className="text-xs text-gray-600 flex items-center gap-2">
                <span className="text-gray-400 w-32 flex-shrink-0">{datumTijd(e.created_at)}</span>
                <span className="font-medium">{e.actor_rol === 'opdrachtgever' ? 'U' : e.actor_rol === 'professional' ? "Zzp'er" : 'Systeem'}</span>
                <span>{e.event_type}</span>
                {e.tarief != null && <span className="font-medium">{euro(e.tarief)}</span>}
                {e.toelichting && <span className="italic truncate">— {e.toelichting}</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OnderhandelingRijKaart;
